import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Form, Spinner, Alert } from 'react-bootstrap';
import {
  fetchSalesCategoryById,
  createSalesCategory,
  updateSalesCategory
} from '../../../services/salesProductService';

const AddEditCategory = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // Category ID for editing

  // Form State
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    status: 'active'
  });

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch existing category if editing
  useEffect(() => {
    if (id) {
      setLoading(true);
      const loadCategory = async () => {
        try {
          const category = await fetchSalesCategoryById(id);
          setFormData({
            name: category.name,
            description: category.description,
            status: category.status
          });
        } catch (error) {
          setError('Failed to load category details. Please try again.');
          console.error('Error fetching category:', error);
        } finally {
          setLoading(false);
        }
      };
      loadCategory();
    } else {
      setLoading(false);
    }
  }, [id]);

  // Handle input change
  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      if (id) {
        await updateSalesCategory(id, formData);
        alert('Category updated successfully');
      } else {
        await createSalesCategory(formData);
        alert('Category added successfully');
      }
      navigate('/inventory/products/all-categories');
    } catch (error) {
      setError('Error submitting category. Please try again.');
      console.error('Error submitting category:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container">
      <h2>{id ? 'Edit' : 'Add'} Category</h2>

      {error && <Alert variant="danger">{error}</Alert>}

      {loading ? (
        <div className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p>Loading category details...</p>
        </div>
      ) : (
        <Form onSubmit={handleSubmit}>
          {/* Category Name */}
          <Form.Group>
            <Form.Label>Category Name</Form.Label>
            <Form.Control
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              disabled={submitting}
            />
          </Form.Group>

          {/* Category Description */}
          <Form.Group>
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={3}
              disabled={submitting}
            />
          </Form.Group>

          {/* Category Status */}
          <Form.Group>
            <Form.Label>Status</Form.Label>
            <Form.Select
              name="status"
              value={formData.status}
              onChange={handleChange}
              disabled={submitting}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </Form.Select>
          </Form.Group>

          {/* Submit Button */}
          <Button type="submit" className="mt-3" disabled={submitting}>
            {submitting ? (
              <>
                <Spinner animation="border" size="sm" /> Saving...
              </>
            ) : (
              `${id ? 'Update' : 'Add'} Category`
            )}
          </Button>
        </Form>
      )}
    </div>
  );
};

export default AddEditCategory;
